import { SceneObjectData } from "../scenedata/sceneobjectdata";
import { SceneInfo } from "../scenedata/sceneinfo";
import { TraceSceneInfoFile, SceneDataWithPreview } from "../scenedata/infofiledata";
// Direct imports
import { Engine } from "@babylonjs/core/Engines/engine";
import { Camera } from "@babylonjs/core/Cameras/camera";
import { ScreenshotTools } from "@babylonjs/core/Misc/screenshotTools";
import { Logger } from './Logger';
import { mimeTypeFromExtension } from './AssetTypes';

// 2025
// Author: Aaron Post

/**
 * Result from parsing info.json, containing both the converted SceneInfo
 * and the original parsed data for export round-tripping.
 */
export interface ParsedInfoJson {
    sceneInfo: SceneInfo;
    sourceInfoFile: TraceSceneInfoFile;
}

export class Utility {
    /**
     * Generate a unique name using "(1)", "(2)", etc. suffix.
     * Strips existing "(N)" suffix from base name to avoid "Name (1) (2)".
     *
     * @param baseName - The original name
     * @param existingNames - Set of existing names to avoid
     * @returns A unique name
     */
    public static generateUniqueName(baseName: string, existingNames: Set<string>): string {
        // Return as-is if no conflict
        if (!existingNames.has(baseName)) return baseName;

        // Strip existing (N) suffix to get the true base name
        const match = baseName.match(/^(.+?)\s*\((\d+)\)$/);
        const trueName = match ? match[1] : baseName;

        // Find the next available number
        let counter = 1;
        let uniqueName = `${trueName} (${counter})`;
        while (existingNames.has(uniqueName)) {
            counter++;
            uniqueName = `${trueName} (${counter})`;
        }
        return uniqueName;
    }

    /**
     * Infer a MIME type from a filename extension.
     * Safari requires a correct MIME type on blob URLs for video/audio playback.
     */
    public static mimeTypeFromFilename(filename: string): string | undefined {
        const ext = Utility.getFileExtensionFromSrc(filename);
        return mimeTypeFromExtension(ext);
    }

    /**
     * The extension of a URI's LAST PATH SEGMENT (i.e. mp4, mp3, jpg, glb),
     * lowercased and without the dot. `''` when that segment has none.
     *
     * The path split is not cosmetic. A library reference is
     * `https://<api>.azurewebsites.net/assets/<guid>/file` (CONTENT_MODEL_SPEC
     * §3.4) — no extension at all — and a naive last-dot scan answers
     * `net/assets/<guid>/file`, inheriting the dot from the HOST. That string
     * then travelled as a `pluginExtension` into Babylon's loader registry and
     * came back as `No plugin or fallback for .net/assets/<guid>/file`.
     * Extensions never contain `/`; anything that would is no extension.
     */
    public static getFileExtensionFromSrc(src: string): string {
        if (!src) {
            return '';
        }
        const path = src.split(/[?#]/)[0];
        const segment = path.substring(path.lastIndexOf('/') + 1);
        const dot = segment.lastIndexOf('.');
        return dot > 0 ? segment.substring(dot + 1).toLowerCase() : '';
    }

    /**
     * JSON.parse for legacy `.trace` / `.zip` scene files. Some 2024 exports
     * carry trailing commas, which the Unity app's Newtonsoft reader accepts,
     * so those scenes play there. A strict parse is tried first; on failure the
     * commas that close an object or array (outside strings) are dropped and the
     * parse is retried once. Anything else malformed throws the strict error.
     */
    public static parseLegacyJson(text: string): unknown {
        try {
            return JSON.parse(text);
        } catch (strictError) {
            let out = '';
            let inString = false;
            for (let i = 0; i < text.length; i++) {
                const c = text[i];
                if (inString) {
                    out += c;
                    if (c === '\\') { out += text[++i] ?? ''; }
                    else if (c === '"') { inString = false; }
                    continue;
                }
                if (c === '"') { inString = true; out += c; continue; }
                if (c === ',') {
                    let j = i + 1;
                    while (j < text.length && /\s/.test(text[j])) j++;
                    if (text[j] === '}' || text[j] === ']') continue;
                }
                out += c;
            }
            try {
                const parsed: unknown = JSON.parse(out);
                Logger.warn('Legacy scene JSON has trailing commas; ignoring them');
                return parsed;
            } catch {
                throw strictError;
            }
        }
    }

    /**
     * Parses info.json file and converts to SceneInfo format
     *
     * info.json uses 'traces' property and may include preview data (camera poses, preview images).
     * This converts it to the standard SceneInfo format with 'content' property.
     *
     * @param text - Raw JSON string from info.json file
     * @returns ParsedInfoJson with sceneInfo and sourceInfoFile, or null if invalid
     */
    public static parseInfoJson(text: string): ParsedInfoJson | null {
        Logger.log("Parsing info.json");

        let parsedData: TraceSceneInfoFile;
        try {
            parsedData = Utility.parseLegacyJson(text) as TraceSceneInfoFile;
        } catch (error) {
            Logger.error("Failed to parse info.json:", error);
            return null;
        }

        if (!parsedData.traces || parsedData.traces.length === 0) {
            Logger.log("No traces found in info.json");
            return null;
        }

        // Convert traces with preview data to standard SceneData format
        const content: SceneObjectData[] = parsedData.traces.map((trace: SceneDataWithPreview) => {
            const sceneData: SceneObjectData = { ...trace };

            // If preview data exists, use it to override src and transform
            if (trace.preview) {
                sceneData.src = trace.preview.src;

                const { position, rotation, scale } = trace.preview.pose;
                sceneData.transform = {
                    translation: position,
                    rotation: rotation,
                    scale: scale,
                };
            }

            return sceneData;
        });

        return { sceneInfo: { content }, sourceInfoFile: parsedData };
    }

    public static async captureScreenshot(
        engine: Engine,
        camera: Camera,
        mimeType: string
    ): Promise<Blob> {
        try {
            engine.flushFramebuffer();

            const dataUrl = await ScreenshotTools.CreateScreenshotAsync(
                engine,
                camera,
                { precision: 1.0 },
                mimeType
            );

            Logger.log("Screenshot captured successfully!");
            return Utility.base64ToBlob(dataUrl, mimeType);

        } catch (error) {
            Logger.error("Error during screenshot capture:", error);
            throw new Error("Failed to capture screenshot");
        }
    }
    
    private static base64ToBlob(base64Data: string, contentType: string = ""): Blob {
        const base64 = base64Data.split(",")[1];
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    }

    public static async isBlobGLB(assetURL: string) {
        if (!assetURL.startsWith('blob:')) return false;
    
        try {
            const response = await fetch(assetURL);
            const contentType = response.headers.get('Content-Type');
            Logger.log('Blob content type:', contentType);
            return contentType === 'model/gltf-binary';
        } catch (error) {
            Logger.error('Error checking blob content type:', error);
            return false;
        }
    }
}